'use client';

import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import dynamic from 'next/dynamic';
import {
  requestRegisterOptions,
  verifyRegistration,
  requestLoginOptions,
  verifyLogin,
  sendMagicLink,
} from '@/services/auth-v2.service';

// FaultyTerminal uses WebGL / window — load client-side only
const FaultyTerminal = dynamic(
  () => import('@/components/FaultyTerminal'),
  { ssr: false },
);

// Memoized so the 1s clock tick doesn't re-mount the WebGL context
const ShaderBackground = memo(function ShaderBackground() {
  return (
    <div className="fixed inset-0 z-0" aria-hidden="true" style={{ opacity: 0.55 }}>
      <FaultyTerminal
        scale={1.5}
        digitSize={1.2}
        scanlineIntensity={0.5}
        glitchAmount={1}
        flickerAmount={1}
        noiseAmp={1}
        chromaticAberration={0}
        dither={0}
        curvature={0.1}
        tint="#f0e6a3"
        mouseReact
        mouseStrength={0.5}
        brightness={0.6}
        className="w-full h-full"
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
});


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FingerprintIcon({ active, size = 44 }: { active?: boolean; size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      aria-hidden="true"
      className={`transition-all duration-300 ${active ? 'scale-110' : ''}`}
      fill={active ? '#c8ff5e' : 'currentColor'}
      style={{ color: active ? '#c8ff5e' : 'var(--text-secondary)' }}
    >
      <path d="M 8 3 L 8 4 L 8 15 L 12 15 L 12 27 L 14 27 L 14 15 L 18 15 L 18 19 L 20 19 L 20 15 L 24 15 L 24 3 L 8 3 z M 18 19 L 16 19 L 16 21 L 18 21 L 18 19 z M 18 21 L 18 23 L 20 23 L 20 21 L 18 21 z M 18 23 L 16 23 L 16 25 L 18 25 L 18 23 z M 18 25 L 18 27 L 14 27 L 14 29 L 20 29 L 20 25 L 18 25 z M 10 5 L 22 5 L 22 13 L 10 13 L 10 5 z M 13 7 L 13 9 L 19 9 L 19 7 L 13 7 z" />
    </svg>
  );
}

interface PasskeyButtonProps {
  onTrigger: () => Promise<void>;
  loading?: boolean;
  label?: string;
}

function PasskeyButton({ onTrigger, loading: externalLoading, label = 'Sign in with passkey' }: PasskeyButtonProps) {
  const loading = externalLoading ?? false;
  const [hovered, setHovered] = useState(false);
  const [sweepPos, setSweepPos] = useState(0);
  const sweepRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function handleClick() {
    if (loading) return;
    void onTrigger();
  }

  useEffect(() => {
    if (hovered && !loading) {
      sweepRef.current = setInterval(() => {
        setSweepPos(p => (p + 2) % 120);
      }, 16);
    } else {
      if (sweepRef.current) clearInterval(sweepRef.current);
      setSweepPos(0);
    }
    return () => {
      if (sweepRef.current) clearInterval(sweepRef.current);
    };
  }, [hovered, loading]);

  // Build the sweep gradient string
  const sweepA = String(sweepPos - 20);
  const sweepB = String(sweepPos);
  const sweepC = String(sweepPos + 20);
  const sweepGradient =
    `linear-gradient(105deg, transparent ${sweepA}%, rgba(200,255,94,0.06) ${sweepB}%, transparent ${sweepC}%)`;

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => { setHovered(true); }}
      onMouseLeave={() => { setHovered(false); }}
      disabled={loading}
      aria-label={label}
      className={[
        'relative w-full overflow-hidden',
        'flex flex-col items-center',
        'rounded-2xl border',
        'pt-12 pb-8 px-6',
        'transition-all duration-300 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff5e]',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c0c0c]',
        loading
          ? 'border-[#c8ff5e]/60 bg-[#c8ff5e]/[0.06] cursor-not-allowed'
          : hovered
            ? 'border-[#c8ff5e]/70 bg-[#c8ff5e]/[0.05] cursor-pointer'
            : 'border-white/10 bg-white/[0.02] cursor-pointer',
      ].join(' ')}
    >
      {/* Sweep shimmer on hover */}
      {hovered && !loading && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: sweepGradient }}
        />
      )}

      {/* Icon with animated rings */}
      <span className="relative flex items-center justify-center w-[106px] h-[106px] mb-6">
        <span
          aria-hidden="true"
          className={[
            'absolute rounded-full border transition-all duration-700',
            loading
              ? 'w-[106px] h-[106px] border-[#c8ff5e]/30 animate-ping'
              : hovered
                ? 'w-[95px] h-[95px] border-[#c8ff5e]/20 animate-pulse'
                : 'w-[85px] h-[85px] border-white/10',
          ].join(' ')}
        />
        <span
          aria-hidden="true"
          className={[
            'absolute rounded-full border transition-all duration-500',
            loading
              ? 'w-[74px] h-[74px] border-[#c8ff5e]/20'
              : hovered
                ? 'w-[64px] h-[64px] border-[#c8ff5e]/10'
                : 'w-[53px] h-[53px] border-white/[0.06]',
          ].join(' ')}
        />
        {loading && (
          <span
            aria-hidden="true"
            className="absolute w-[85px] h-[85px] rounded-full border-2 border-transparent border-t-[#c8ff5e]/60 animate-spin"
          />
        )}
        <span
          className={[
            'relative z-10 transition-colors duration-200',
            loading || hovered ? 'text-[#c8ff5e]' : 'text-white/70',
          ].join(' ')}
        >
          <FingerprintIcon active={loading || hovered} size={36} />
        </span>
      </span>

      <span
        className={[
          'text-[10px] tracking-[0.2em] uppercase font-medium transition-colors duration-200',
          loading
            ? 'text-[#c8ff5e]/80'
            : hovered
              ? 'text-[#c8ff5e]/90'
              : 'text-white/50',
        ].join(' ')}
      >
        {loading ? 'Waiting for passkey\u2026' : label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Magic Link Form
// ---------------------------------------------------------------------------

function MagicLinkForm({ onStatusChange }: { onStatusChange: (msg: string) => void }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || sending) return;

    setError('');
    setSending(true);
    onStatusChange('SENDING_EMAIL');

    try {
      await sendMagicLink(email);
      setSent(true);
      onStatusChange('EMAIL_SENT');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send email';
      setError(msg);
      onStatusChange('ERROR');
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[#c8ff5e]/20 bg-[#c8ff5e]/[0.06] mb-4">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-[#c8ff5e]">
            <path d="M2 4L10 10L18 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        </div>
        <p className="text-[13px] text-white/60 mb-1">Check your email</p>
        <p className="text-[11px] text-white/30">
          We sent a sign-in link to <span className="text-white/50">{email}</span>
        </p>
        <button
          type="button"
          onClick={() => { setSent(false); setEmail(''); }}
          className="mt-4 text-[10px] tracking-[0.15em] uppercase text-white/30 hover:text-white/50 transition-colors"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={e => { void handleSubmit(e); }} className="space-y-3">
      <div className="relative">
        <input
          type="email"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(''); }}
          placeholder="you@email.com"
          required
          className={[
            'w-full px-4 py-3 rounded-xl border bg-white/[0.03] text-[13px] text-white/80',
            'placeholder:text-white/20 outline-none transition-all duration-200',
            'focus:border-[#c8ff5e]/40 focus:ring-1 focus:ring-[#c8ff5e]/20',
            error ? 'border-red-500/40' : 'border-white/10',
          ].join(' ')}
        />
      </div>
      {error && (
        <p className="text-[11px] text-red-400/80">{error}</p>
      )}
      <button
        type="submit"
        disabled={sending || !email}
        className={[
          'w-full py-3 rounded-xl border text-[11px] tracking-[0.15em] uppercase font-medium',
          'transition-all duration-200',
          sending || !email
            ? 'border-white/[0.06] bg-white/[0.02] text-white/20 cursor-not-allowed'
            : 'border-white/10 bg-white/[0.04] text-white/50 hover:border-[#c8ff5e]/30 hover:text-[#c8ff5e]/70',
        ].join(' ')}
      >
        {sending ? 'Sending\u2026' : 'Continue with email'}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Divider
// ---------------------------------------------------------------------------

function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-5">
      <span className="flex-1 h-px bg-white/[0.06]" />
      <span className="text-[9px] tracking-[0.25em] uppercase text-white/20">or</span>
      <span className="flex-1 h-px bg-white/[0.06]" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type Tab = 'sign_in' | 'create_account';

export default function AuthPage() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<Tab>('sign_in');
  const [statusMsg, setStatusMsg] = useState('READY');
  const [error, setError] = useState('');
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [registerEmail, setRegisterEmail] = useState('');
  const [, setTick] = useState(0);
  const [timeStr, setTimeStr] = useState('');

  // Live clock
  useEffect(() => {
    function updateClock() {
      setTimeStr(new Date().toTimeString().slice(0, 8));
    }
    updateClock();
    const interval = setInterval(() => {
      setTick(t => t + 1);
      updateClock();
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const handlePasskeyLogin = useCallback(async () => {
    setError('');
    setPasskeyLoading(true);
    setStatusMsg('AUTHENTICATING');

    try {
      // 1. Get challenge from backend
      const { challengeId, options } = await requestLoginOptions();

      // 2. Browser WebAuthn prompt
      setStatusMsg('PASSKEY_PROMPT');
      const assertion = await startAuthentication({ optionsJSON: options });

      // 3. Verify with backend
      setStatusMsg('VERIFYING');
      const { session, user } = await verifyLogin({ challengeId, assertion });

      // 4. Create NextAuth session
      setStatusMsg('CREATING_SESSION');
      const result = await signIn('passkey-v2', {
        redirect: false,
        session,
        userId: user.id,
        email: user.email ?? '',
        hasWallet: user.hasWallet ? 'true' : 'false',
        smartAccountAddress: user.smart_account_address ?? '',
        credentialId: assertion.id,
        passkeyTransports: JSON.stringify(user.transports ?? []),
      });

      if (result.error) {
        throw new Error(result.error);
      }

      setStatusMsg('AUTHENTICATED');
      router.push('/portfolio');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Authentication failed';
      // WebAuthn user cancellation
      if (msg.includes('AbortError') || msg.includes('NotAllowedError') || msg.includes('cancelled') || msg.includes('canceled')) {
        setStatusMsg('READY');
        setPasskeyLoading(false);
        return;
      }
      setError(msg);
      setStatusMsg('ERROR');
      setPasskeyLoading(false);
    }
  }, [router]);

  const handlePasskeyRegister = useCallback(async () => {
    setError('');
    setPasskeyLoading(true);
    setStatusMsg('REGISTERING');

    try {
      // 1. Get creation options from backend (pass email for passkey display name)
      const { challengeId, options, userHandle } = await requestRegisterOptions(registerEmail || undefined);

      // 2. Browser WebAuthn prompt
      setStatusMsg('PASSKEY_PROMPT');
      const attestation = await startRegistration({ optionsJSON: options });

      // 3. Verify with backend — creates user account
      setStatusMsg('CREATING_ACCOUNT');
      const { session, user } = await verifyRegistration({
        challengeId,
        attestation,
        userHandle,
        email: registerEmail || undefined,
      });

      // 4. Create NextAuth session
      setStatusMsg('CREATING_SESSION');
      const result = await signIn('passkey-v2', {
        redirect: false,
        session,
        userId: user.id,
        email: registerEmail,
        hasWallet: user.hasWallet ? 'true' : 'false',
        p256PublicKeyHex: user.p256PublicKeyHex,
        credentialId: attestation.id,
        passkeyTransports: JSON.stringify(user.transports ?? attestation.response.transports ?? []),
      });

      if (result.error) {
        throw new Error(result.error);
      }

      setStatusMsg('AUTHENTICATED');
      router.push('/portfolio');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Registration failed';
      if (msg.includes('AbortError') || msg.includes('NotAllowedError') || msg.includes('cancelled') || msg.includes('canceled')) {
        setStatusMsg('READY');
        setPasskeyLoading(false);
        return;
      }
      setError(msg);
      setStatusMsg('ERROR');
      setPasskeyLoading(false);
    }
  }, [router, registerEmail]);

  function handleTabChange(id: Tab) {
    setActiveTab(id);
    setError('');
    setStatusMsg('READY');
  }

  const TABS = [
    { id: 'sign_in' as const,        label: 'SIGN_IN' },
    { id: 'create_account' as const, label: 'CREATE_ACCOUNT' },
  ];

  return (
    <div>
      {/* WebGL Background */}
      <ShaderBackground />

      {/* Radial vignette */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 70% at 50% 50%, transparent 0%, #080808 75%)',
        }}
      />

      {/* Nav bar */}
      <header
        className="fixed top-0 inset-x-0 z-30 h-12 flex items-center justify-between px-6"
        style={{
          backdropFilter: 'blur(12px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(12px) saturate(1.4)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          background: 'rgba(8,8,8,0.72)',
        }}
      >
        <div className="flex items-center gap-2 select-none" aria-label="Hoops Finance">
          <span className="text-[11px] tracking-[0.28em] uppercase text-white/60 font-medium">
            HOOPS
          </span>
          <span className="text-white/20 text-[10px]">//</span>
          <span className="text-[11px] tracking-[0.28em] uppercase text-[#c8ff5e]/70 font-medium">
            AUTH
          </span>
        </div>

        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/[0.07] bg-white/[0.03]"
          aria-label="Connected to testnet"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#c8ff5e] animate-pulse" aria-hidden="true" />
          <span className="text-[10px] tracking-[0.15em] text-white/40 uppercase">testnet</span>
        </div>
      </header>

      {/* Page body */}
      <main className="relative z-20 min-h-screen flex items-center justify-center px-4 pt-12 pb-10">

        {/* Card */}
        <div
          className="w-full max-w-[400px]"
          style={{
            background: 'rgba(10,10,10,0.82)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '20px',
            backdropFilter: 'blur(24px) saturate(1.6)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
            boxShadow:
              '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03) inset',
          }}
        >
          {/* Card header */}
          <div className="px-7 pt-8 pb-6 border-b border-white/[0.06]">
            <p
              className="text-[10px] tracking-[0.3em] uppercase font-medium mb-4"
              style={{ color: '#c8ff5e', opacity: 0.8 }}
            >
              HOOPS // AUTH
            </p>

            <h1
              className="text-[28px] leading-[1.15] font-normal text-white/90 mb-2"
              style={{ fontFamily: 'var(--font-instrument-serif), Georgia, serif' }}
            >
              Access your
              <br />
              <em className="not-italic" style={{ color: '#c8ff5e', opacity: 0.9 }}>
                smart account.
              </em>
            </h1>

            <p className="text-[12px] leading-relaxed text-white/35 mt-3">
              Non-custodial. Your keys, secured by your device.
            </p>
          </div>

          {/* Tab strip */}
          <div
            className="flex px-7 pt-5"
            role="tablist"
            aria-label="Auth options"
          >
            {TABS.map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`tabpanel-${tab.id}`}
                id={`tab-${tab.id}`}
                onClick={() => { handleTabChange(tab.id); }}
                className={[
                  'flex-1 pb-3 text-[10px] tracking-[0.2em] font-medium uppercase',
                  'border-b-2 transition-all duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff5e]',
                  'focus-visible:ring-offset-1 focus-visible:ring-offset-[#0a0a0a]',
                  activeTab === tab.id
                    ? 'border-[#c8ff5e] text-[#c8ff5e]'
                    : 'border-white/10 text-white/30 hover:text-white/50',
                ].join(' ')}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab panels */}
          <div className="px-7 pt-6 pb-7">

            {/* Error banner */}
            {error && (
              <div className="mb-4 px-4 py-3 rounded-xl border border-red-500/20 bg-red-500/[0.06]">
                <p className="text-[11px] text-red-400/80">{error}</p>
              </div>
            )}

            {/* SIGN IN */}
            <div
              id="tabpanel-sign_in"
              role="tabpanel"
              aria-labelledby="tab-sign_in"
              hidden={activeTab !== 'sign_in'}
            >
              <PasskeyButton
                label="Sign in with passkey"
                loading={passkeyLoading}
                onTrigger={handlePasskeyLogin}
              />

              <OrDivider />

              <MagicLinkForm onStatusChange={setStatusMsg} />
            </div>

            {/* CREATE ACCOUNT */}
            <div
              id="tabpanel-create_account"
              role="tabpanel"
              aria-labelledby="tab-create_account"
              hidden={activeTab !== 'create_account'}
            >
              {/* Info pill */}
              <div className="mb-5 px-4 py-3 rounded-xl border border-[#c8ff5e]/15 bg-[#c8ff5e]/[0.04]">
                <p className="text-[11px] leading-relaxed text-white/50">
                  Hoops uses{' '}
                  <span className="text-[#c8ff5e]/80 font-medium">passkeys</span>{' '}
                  — hardware-backed credentials stored on your device. No seed phrase.
                  No password. Your biometric is the key.
                </p>
              </div>

              {/* Optional email — sets the passkey display name */}
              <div className="mb-4">
                <input
                  type="email"
                  value={registerEmail}
                  onChange={e => { setRegisterEmail(e.target.value); }}
                  placeholder="Email (optional — labels your passkey)"
                  className={[
                    'w-full px-4 py-3 rounded-xl border bg-white/[0.03] text-[13px] text-white/80',
                    'placeholder:text-white/20 outline-none transition-all duration-200',
                    'focus:border-[#c8ff5e]/40 focus:ring-1 focus:ring-[#c8ff5e]/20',
                    'border-white/10',
                  ].join(' ')}
                />
              </div>

              <PasskeyButton
                label="Create passkey account"
                loading={passkeyLoading}
                onTrigger={handlePasskeyRegister}
              />

              <OrDivider />

              <MagicLinkForm onStatusChange={setStatusMsg} />

              <p className="mt-4 text-[10px] leading-relaxed text-white/25 text-center">
                A smart contract account is deployed on Stellar testnet.
                <br />
                This creates a secp256r1 key on-chain.
              </p>
            </div>

            {/* Terms */}
            <p className="mt-6 text-[10px] text-white/20 text-center leading-relaxed">
              By continuing you agree to the{' '}
              <a
                href="/tos"
                className="underline underline-offset-2 text-white/30 hover:text-white/50 transition-colors"
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="/privacy"
                className="underline underline-offset-2 text-white/30 hover:text-white/50 transition-colors"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </main>

      {/* Status bar */}
      <footer
        className="fixed bottom-0 inset-x-0 z-30 h-8 flex items-center justify-between px-6"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          background: 'rgba(6,6,6,0.85)',
        }}
        aria-label="Status bar"
      >
        <span className="text-[10px] tracking-[0.18em] text-white/25 uppercase">
          stellar · testnet
        </span>

        <span
          className="text-[10px] tracking-[0.22em] text-white/40 uppercase transition-all duration-300"
          aria-live="polite"
          aria-atomic="true"
        >
          {statusMsg}
        </span>

        <span className="text-[10px] tracking-[0.12em] text-white/20 tabular-nums">
          {timeStr}
        </span>
      </footer>
    </div>
  );
}
